import { createSlice } from "@reduxjs/toolkit";

// Helper function to safely parse localStorage arrays
const safeParseArray = (key) => {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.warn(`Failed to parse ${key} from localStorage:`, error);
        return [];
    }
};

const initialState = {
    // P2P Marketplace data
    offers: safeParseArray('p2pOffers'),
    myOrders: safeParseArray('p2pMyOrders'),
    orderHistory: safeParseArray('p2pOrderHistory'),
    
    // P2P Chat data
    chatSessions: JSON.parse(localStorage.getItem('p2pChatSessions')) || {},
    activeChatId: null,
    
    // P2P Statistics
    statistics: JSON.parse(localStorage.getItem('p2pStatistics')) || null,
    
    // UI State
    loading: false,
    error: null,
    
    // Filters and pagination
    filters: {
        tradeType: 'buy',
        cryptocurrency: 'USDT',
        fiatCurrency: 'USD',
        paymentMethod: '',
        amount: '',
        minAmount: '',
        maxAmount: ''
    },
    pagination: {
        currentPage: 1,
        totalPages: 1,
        perPage: 20
    }
}

export const p2pSlice = createSlice({
    name: 'p2p',
    initialState,
    reducers: {
        // Offers management
        setOffers: (state, action) => {
            state.offers = action.payload;
            localStorage.setItem('p2pOffers', JSON.stringify(state.offers));
        },
        
        addOffer: (state, action) => {
            state.offers.unshift(action.payload);
            localStorage.setItem('p2pOffers', JSON.stringify(state.offers));
        },
        
        updateOffer: (state, action) => {
            const index = state.offers.findIndex(offer => offer.id === action.payload.id);
            if (index !== -1) {
                state.offers[index] = action.payload;
                localStorage.setItem('p2pOffers', JSON.stringify(state.offers));
            }
        },
        
        removeOffer: (state, action) => {
            state.offers = state.offers.filter(offer => offer.id !== action.payload);
            localStorage.setItem('p2pOffers', JSON.stringify(state.offers));
        },

        // Orders management
        setMyOrders: (state, action) => {
            // Ensure payload is always an array
            const orders = Array.isArray(action.payload) ? action.payload : [];
            state.myOrders = orders;
            localStorage.setItem('p2pMyOrders', JSON.stringify(state.myOrders));
        },
        
        addOrder: (state, action) => {
            state.myOrders.unshift(action.payload);
            localStorage.setItem('p2pMyOrders', JSON.stringify(state.myOrders));
        },
        
        updateOrder: (state, action) => {
            const index = state.myOrders.findIndex(order => order.id === action.payload.id);
            if (index !== -1) {
                state.myOrders[index] = action.payload;
                localStorage.setItem('p2pMyOrders', JSON.stringify(state.myOrders));
            }
            
            // Also update in history if exists
            const historyIndex = state.orderHistory.findIndex(order => order.id === action.payload.id);
            if (historyIndex !== -1) {
                state.orderHistory[historyIndex] = action.payload;
                localStorage.setItem('p2pOrderHistory', JSON.stringify(state.orderHistory));
            }
        },

        // Order History
        setOrderHistory: (state, action) => {
            state.orderHistory = action.payload;
            localStorage.setItem('p2pOrderHistory', JSON.stringify(state.orderHistory));
        },

        // Chat management
        setChatSessions: (state, action) => {
            state.chatSessions = action.payload;
            localStorage.setItem('p2pChatSessions', JSON.stringify(state.chatSessions));
        },
        
        updateChatSession: (state, action) => {
            const { orderId, messages, lastMessage, unreadCount } = action.payload;
            state.chatSessions[orderId] = {
                ...state.chatSessions[orderId],
                messages: messages || state.chatSessions[orderId]?.messages || [],
                lastMessage: lastMessage || state.chatSessions[orderId]?.lastMessage,
                unreadCount: unreadCount !== undefined ? unreadCount : state.chatSessions[orderId]?.unreadCount || 0,
                lastUpdated: new Date().toISOString()
            };
            localStorage.setItem('p2pChatSessions', JSON.stringify(state.chatSessions));
        },
        
        addChatMessage: (state, action) => {
            const { orderId, message } = action.payload;
            if (!state.chatSessions[orderId]) {
                state.chatSessions[orderId] = { messages: [], unreadCount: 0 };
            }
            state.chatSessions[orderId].messages.push(message);
            state.chatSessions[orderId].lastMessage = message;
            state.chatSessions[orderId].lastUpdated = new Date().toISOString();
            
            // Increment unread count if message is not from current user
            if (!message.is_from_current_user) {
                state.chatSessions[orderId].unreadCount = (state.chatSessions[orderId].unreadCount || 0) + 1;
            }
            
            localStorage.setItem('p2pChatSessions', JSON.stringify(state.chatSessions));
        },
        
        markChatAsRead: (state, action) => {
            const orderId = action.payload;
            if (state.chatSessions[orderId]) {
                state.chatSessions[orderId].unreadCount = 0;
                localStorage.setItem('p2pChatSessions', JSON.stringify(state.chatSessions));
            }
        },
        
        setActiveChatId: (state, action) => {
            state.activeChatId = action.payload;
        },

        // Statistics
        setStatistics: (state, action) => {
            state.statistics = action.payload;
            localStorage.setItem('p2pStatistics', JSON.stringify(state.statistics));
        },

        // UI State
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        
        setError: (state, action) => {
            state.error = action.payload;
        },
        
        clearError: (state) => {
            state.error = null;
        },

        // Filters
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        
        resetFilters: (state) => {
            state.filters = initialState.filters;
        },

        // Pagination
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },

        // Clear all data (for logout)
        clearP2PData: (state) => {
            Object.keys(initialState).forEach(key => {
                if (key !== 'filters') {
                    state[key] = initialState[key];
                }
            });
            
            // Clear localStorage
            localStorage.removeItem('p2pOffers');
            localStorage.removeItem('p2pMyOrders');
            localStorage.removeItem('p2pOrderHistory');
            localStorage.removeItem('p2pChatSessions');
            localStorage.removeItem('p2pStatistics');
        }
    }
})

export const {
    // Offers
    setOffers,
    addOffer,
    updateOffer,
    removeOffer,
    
    // Orders
    setMyOrders,
    addOrder,
    updateOrder,
    setOrderHistory,
    
    // Chat
    setChatSessions,
    updateChatSession,
    addChatMessage,
    markChatAsRead,
    setActiveChatId,
    
    // Statistics
    setStatistics,
    
    // UI State
    setLoading,
    setError,
    clearError,
    
    // Filters
    setFilters,
    resetFilters,
    
    // Pagination
    setPagination,
    
    // Utility
    clearP2PData
} = p2pSlice.actions;

export default p2pSlice.reducer;
